import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, CircleX, Play, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, ApiError } from "@/lib/api";
import { RelativeTime } from "@/components/RelativeTime";

interface CheckResult {
  id: string;
  name: string;
  path: string | null;
  status: "pass" | "fail" | "error";
  httpStatus: number | null;
  detail: string | null;
  durationMs: number | null;
  createdAt: string;
}

/**
 * 006 US10 — per-deployment check results. Config lives in project
 * Settings; failing results block promote (with an audited override).
 */
export function DeploymentChecks({ deploymentId, ready }: { deploymentId: string; ready: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["deployment-checks", deploymentId],
    queryFn: () => api.get<{ results: CheckResult[]; history: CheckResult[] }>(`/deployments/${deploymentId}/checks`),
    refetchInterval: (query) =>
      // Poll briefly after a re-run kicks off; settle once results exist.
      query.state.data && query.state.data.results.length > 0 ? false : 8000,
  });

  const rerun = useMutation({
    mutationFn: () => api.post(`/deployments/${deploymentId}/checks/run`),
    onSuccess: () => {
      setTimeout(() => qc.invalidateQueries({ queryKey: ["deployment-checks", deploymentId] }), 3000);
    },
  });

  const results = q.data?.results ?? [];

  return (
    <div className="of-card overflow-hidden">
      <div className="of-card-header flex-wrap gap-y-2">
        <h3 className="text-sm font-semibold">Checks</h3>
        <div className="flex flex-wrap items-center justify-end gap-2 min-w-0">
          {rerun.error instanceof ApiError && (
            <span className="text-xs text-danger min-w-0">{rerun.error.message}</span>
          )}
          <Button variant="default" size="sm" onClick={() => rerun.mutate()} disabled={!ready || rerun.isPending}>
            {rerun.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            Run checks
          </Button>
        </div>
      </div>
      {q.isLoading && (
        <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</div>
      )}
      {!q.isLoading && results.length === 0 && (
        <div className="px-4 py-10 text-center text-sm text-muted-foreground">
          No check results yet. Configure checks in Settings — they run automatically after each build
          {ready ? ", or run them now" : ""}.
        </div>
      )}
      {results.map((r) => (
        <div key={r.name} className="of-row gap-3 items-start">
          {r.status === "pass" ? (
            <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" />
          ) : r.status === "fail" ? (
            <CircleX className="h-4 w-4 text-danger shrink-0 mt-0.5" />
          ) : (
            <CircleAlert className="h-4 w-4 text-attention shrink-0 mt-0.5" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium break-words">
              {r.name}
              {r.path ? <span className="font-mono text-xs text-muted-foreground ml-2 break-all">{r.path}</span> : null}
            </div>
            {r.detail && <div className="text-xs text-muted-foreground mt-0.5 break-words">{r.detail}</div>}
          </div>
          <div className="text-2xs text-subtle-foreground text-right whitespace-nowrap">
            {r.httpStatus ? <span className="font-mono">{r.httpStatus}</span> : null}
            {r.durationMs !== null ? <span className="font-mono ml-2">{r.durationMs}ms</span> : null}
            <div>
              <RelativeTime value={r.createdAt} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
